import React, { useState } from "react";
import { useDispatch } from "react-redux";
import log from "loglevel";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addTeamUserAction,
  deleteTeamUserAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  TeamUser,
  AccountUser,
  Team,
  Role,
  AddTeamUserRequestType,
  RemoveTeamUserRequestType,
} from "types";
import {
  generateAccountUserDropdownItems,
  generateTeamDropdownItems,
  generateRoleDropdownItems,
} from "util/dashboardUtil";
import TeamUsersTable from "./TeamUsersTable";
import genStyles from "styles/General.module.scss";

type AddTeamUserFormType = {
  team_id: number | undefined;
  account_user_id: number | undefined;
  role_id: number | undefined;
};

const defaultTeamUser: AddTeamUserFormType = {
  team_id: undefined,
  account_user_id: undefined,
  role_id: undefined,
};

const TeamUsersCard = ({
  teamUsers,
  accountUsers,
  teams,
  roles,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  teamUsers: TeamUser[];
  accountUsers: AccountUser[];
  teams: Team[];
  roles: Role[];
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();

  const [selectedTeamUser, setSelectedTeamUser] = useState<TeamUser>();
  const [selectedTeamFromDropdown, setSelectedTeamFromDropdown] = useState<
    Team
  >();
  const [
    selectedAccountUserFromDropdown,
    setSelectedAccountUserFromDropdown,
  ] = useState<AccountUser>();
  const [
    selectedRoleNameFromDropdown,
    setSelectedRoleNameFromDropdown,
  ] = useState<string>();
  const [addTeamUser, setAddTeamUser] = useState<AddTeamUserFormType>(
    defaultTeamUser
  );
  // eslint-disable-next-line
  const [isAddingTeamUser, setIsAddingTeamUser] = useState(false);
  // eslint-disable-next-line
  const [isDeletingTeamUser, setIsDeletingTeamUser] = useState(false);
  //   const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const validateAddTeamUser = (addForm: AddTeamUserFormType | undefined) => {
    if (
      addForm &&
      addForm.team_id &&
      addForm.account_user_id &&
      addForm.role_id
    ) {
      log.info("All fields valid! Button is clickable!");
      return true;
    } else {
      log.info("All fields NOT valid! " + JSON.stringify(addTeamUser));
      return false;
    }
  };

  const onHandleAddTeamUserClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to ADD TeamUser...");
    if (
      addTeamUser?.team_id &&
      addTeamUser?.account_user_id &&
      addTeamUser?.role_id
    ) {
      const request: AddTeamUserRequestType = {
        body: {
          team_id: addTeamUser.team_id,
          account_user_id: addTeamUser.account_user_id,
          role_id: addTeamUser.role_id,
        },
      };
      log.info("-- Dispatching addTeamUserAction()...");
      dispatch(addTeamUserAction(request));
      setIsAddingTeamUser(false);
      setShowCreateModal(false);
      setAddTeamUser(defaultTeamUser); // clear
    } else {
      log.info("One or more fields NOT VALID!");
      log.info("AddTeamUserFormType: " + JSON.stringify(addTeamUser));
    }
  };

  const onHandleRemoveTeamUserClick = (event: any) => {
    event.preventDefault();
    if (selectedTeamUser) {
      const request: RemoveTeamUserRequestType = {
        id: selectedTeamUser.id,
      };
      log.info("-- Dispatching deleteTeamUserAction()...");
      dispatch(deleteTeamUserAction(request));
      setIsDeletingTeamUser(false);
      setShowDeleteModal(false);
      setSelectedTeamUser(undefined); // clear
    }
  };

  const teamUserTableRowEvents = {
    onClick: (e: any, row: TeamUser, rowIndex: number) => {
      log.info("Row Index: " + rowIndex);
      log.info("Row: " + JSON.stringify(row));
      setSelectedTeamUser(row);
      setShowDeleteModal(true);
    },
  };

  const onSelectTeamFromDropdown = (teamItem: any) => {
    if (teamItem) {
      log.info(
        "Add TeamUser: Team Dropdown Item, Selected! : " +
          JSON.stringify(teamItem)
      );
      setAddTeamUser((state): typeof state => ({
        ...state,
        team_id: teamItem.id,
      }));
      // Selecting a Team will filter other dropdowns
      setSelectedTeamFromDropdown(teamItem.team);
    }
  };

  const onSelectAccountUserFromDropdown = (accountUserItem: any) => {
    if (accountUserItem) {
      log.info(
        "Add TeamUser: AccountUser Dropdown Item, Selected! : " +
          JSON.stringify(accountUserItem)
      );
      setAddTeamUser((state): typeof state => ({
        ...state,
        // id of the "Account User", not the "User"
        account_user_id: accountUserItem.accountUser.id,
      }));
      // Selecting a Team will filter other dropdowns
      setSelectedAccountUserFromDropdown(accountUserItem.accountUser);
    }
  };

  const onSelectRoleFromDropdown = (roleItem: any) => {
    if (roleItem) {
      log.info(
        "Add TeamUser: Role Dropdown Item, Selected! : " +
          JSON.stringify(roleItem)
      );
      setAddTeamUser((state): typeof state => ({
        ...state,
        role_id: roleItem.id,
      }));
      setSelectedRoleNameFromDropdown(roleItem.label);
    }
  };

  return (
    <>
      {teamUsers && accountUsers && teams && roles ? (
        <TeamUsersTable
          teamUsers={teamUsers}
          rowEvents={teamUserTableRowEvents}
          showRole={true}
          showAccount={true}
          showTeam={true}
          showSearch={showSearch}
        />
      ) : null}
      {/* Create TeamUser Modal */}
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setAddTeamUser(defaultTeamUser);
            setSelectedTeamFromDropdown(undefined);
            setSelectedAccountUserFromDropdown(undefined);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Add User to Team <FontAwesomeIcon icon={["fal", "users"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-analysis-form">
            <Modal.Body>
              <div className="text-center">
                <div className="form-group">
                  <Select
                    options={generateTeamDropdownItems(teams)}
                    placeholder={"Select Team..."}
                    isClearable={false}
                    onChange={onSelectTeamFromDropdown}
                  />
                </div>
                <div className="form-group">
                  <Select
                    options={generateAccountUserDropdownItems(
                      accountUsers,
                      selectedTeamFromDropdown?.account_id
                    )}
                    placeholder={"Select User..."}
                    isClearable={false}
                    onChange={onSelectAccountUserFromDropdown}
                    isDisabled={selectedTeamFromDropdown === undefined}
                  />
                </div>
                <div className="form-group">
                  <Select
                    options={generateRoleDropdownItems(roles)}
                    placeholder={"Select Role..."}
                    isClearable={false}
                    onChange={onSelectRoleFromDropdown}
                    isDisabled={selectedAccountUserFromDropdown === undefined}
                  />
                </div>
                <div>
                  {validateAddTeamUser(addTeamUser) ? (
                    <p style={{ fontStyle: "italic" }}>
                      {`This will add the user "${selectedAccountUserFromDropdown?.username}" 
                  to the team "${selectedTeamFromDropdown?.name}" in account "${selectedTeamFromDropdown?.account_name}" 
                  with the role of "Team ${selectedRoleNameFromDropdown}"`}
                    </p>
                  ) : null}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Add"
                className={[
                  genStyles["alenza__btn-create__mini"],
                  "float-right",
                ].join(" ")}
                disabled={!validateAddTeamUser(addTeamUser)}
                onClick={(e: any) => {
                  log.info("Add TeamUser Button Clicked!");
                  setIsAddingTeamUser(true);
                  onHandleAddTeamUserClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
      {/* TeamUser Settings Modal */}
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedTeamUser(undefined);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Remove User from Team{" "}
              <FontAwesomeIcon icon={["fal", "user-times"]} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTeamUser ? (
              <>
                <form name="delete-form">
                  <p>
                    Removing user "
                    <span className="font-bold">
                      {selectedTeamUser.username}
                    </span>
                    " from Team "
                    <span className="font-bold">
                      {selectedTeamUser.team_name}
                    </span>
                    " in Account "
                    <span className="font-bold">
                      {selectedTeamUser.account_name}
                    </span>
                    ".
                  </p>
                  <p>Are you sure you want to remove this user?</p>
                </form>
                <input
                  type="submit"
                  value="Remove"
                  className={[
                    genStyles["alenza__btn-delete__mini"],
                    "float-right",
                  ].join(" ")}
                  onClick={(e: any) => {
                    setIsDeletingTeamUser(true);
                    onHandleRemoveTeamUserClick(e);
                  }}
                />
              </>
            ) : (
              <p>
                Unable to remove user from this team at this time. Please try
                again later.
              </p>
            )}
          </Modal.Body>
        </Modal>
      </>
    </>
  );
};

export default TeamUsersCard;
